import React, { useState } from 'react';
import client from '../api/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDesign } from '../contexts/DesignContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/DateInput';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  User, Dumbbell, Target, Settings, ChevronRight, ChevronLeft, Check, Sparkles,
  Scale, Ruler, Calendar, Activity, Clock, Home, Trophy,
  Heart, Zap, Flame, Shield, Award, Crown, Upload
} from 'lucide-react';
import HevyImportWizard from '../components/HevyImportWizard';



const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };

const getSteps = (t) => [
  { id: 'welcome', title: t('onboarding.stepWelcome'), icon: Sparkles },
  { id: 'basic', title: t('onboarding.stepBasic'), icon: User },
  { id: 'experience', title: t('onboarding.stepExperience'), icon: Dumbbell },
  { id: 'goals', title: t('onboarding.stepGoals'), icon: Target },
  { id: 'preferences', title: t('onboarding.stepPreferences'), icon: Settings },
  { id: 'import', title: t('onboarding.stepImport'), icon: Upload },
];

const getExperienceLevels = (t) => [
  { id: 'beginner', label: t('onboarding.expBeginnerLabel'), desc: t('onboarding.expBeginnerDesc'), icon: '🌱' },
  { id: 'intermediate', label: t('onboarding.expIntermediateLabel'), desc: t('onboarding.expIntermediateDesc'), icon: '💪' },
  { id: 'advanced', label: t('onboarding.expAdvancedLabel'), desc: t('onboarding.expAdvancedDesc'), icon: '🏆' },
];

const getGoals = (t) => [
  { id: 'weight_loss', label: t('onboarding.goalWeightLossLabel'), desc: t('onboarding.goalWeightLossDesc'), icon: '🔥' },
  { id: 'muscle_gain', label: t('onboarding.goalMuscleGainLabel'), desc: t('onboarding.goalMuscleGainDesc'), icon: '💪' },
  { id: 'strength', label: t('onboarding.goalStrengthLabel'), desc: t('onboarding.goalStrengthDesc'), icon: '🏋️' },
  { id: 'endurance', label: t('onboarding.goalEnduranceLabel'), desc: t('onboarding.goalEnduranceDesc'), icon: '🏃' },
  { id: 'flexibility', label: t('onboarding.goalFlexibilityLabel'), desc: t('onboarding.goalFlexibilityDesc'), icon: '🧘' },
  { id: 'general_fitness', label: t('onboarding.goalGeneralFitnessLabel'), desc: t('onboarding.goalGeneralFitnessDesc'), icon: '❤️' },
];

const getTargetAreas = (t) => [
  { id: 'chest', label: t('onboarding.areaChest') },
  { id: 'back', label: t('onboarding.areaBack') },
  { id: 'shoulders', label: t('onboarding.areaShoulders') },
  { id: 'upper_arms', label: t('onboarding.areaUpperArms') },
  { id: 'lower_arms', label: t('onboarding.areaLowerArms') },
  { id: 'upper_legs', label: t('onboarding.areaUpperLegs') },
  { id: 'lower_legs', label: t('onboarding.areaLowerLegs') },
  { id: 'waist', label: t('onboarding.areaWaist') },
  { id: 'cardio', label: t('onboarding.areaCardio') },
  { id: 'neck', label: t('onboarding.areaNeck') },
];

const getEquipmentOptions = (t) => [
  { id: 'full_gym', label: t('onboarding.equipFullGymLabel'), desc: t('onboarding.equipFullGymDesc'), icon: '🏋️' },
  { id: 'home_basic', label: t('onboarding.equipHomeBasicLabel'), desc: t('onboarding.equipHomeBasicDesc'), icon: '🏠' },
  { id: 'bodyweight_only', label: t('onboarding.equipBodyweightLabel'), desc: t('onboarding.equipBodyweightDesc'), icon: '👤' },
];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { design } = useDesign();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const STEPS = getSteps(t);
  const EXPERIENCE_LEVELS = getExperienceLevels(t);
  const GOALS = getGoals(t);
  const TARGET_AREAS = getTargetAreas(t);
  const EQUIPMENT_OPTIONS = getEquipmentOptions(t);

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';
  const hf = design.heading_font || 'Barlow Condensed';
  const [formData, setFormData] = useState({
    birth_date: '',
    gender: '',
    height_cm: '',
    current_weight_kg: '',
    experience_level: '',
    workout_frequency: 3,
    primary_goal: '',
    target_areas: [],
    preferred_workout_duration: 60,
    equipment_access: '',
  });

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTargetArea = (areaId) => {
    setFormData(prev => ({
      ...prev,
      target_areas: prev.target_areas.includes(areaId)
        ? prev.target_areas.filter(a => a !== areaId)
        : [...prev.target_areas, areaId]
    }));
  };

  const canProceed = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return true;
      case 'basic':
        return formData.gender && formData.height_cm;
      case 'experience':
        return formData.experience_level;
      case 'goals':
        return formData.primary_goal;
      case 'preferences':
        return formData.equipment_access;
      case 'import':
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = async () => {
    try {
      await client.post('/onboarding/skip');
      // Refresh the user so onboarding_completed is current, then navigate
      // client-side — a full window.location reload re-boots every context
      // provider and shows a blank screen for ~1s right after signup.
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding skip error:', error);
      const msg = error.response?.data?.detail || t('errors.generic');
      toast.error(msg);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const heightVal = formData.height_cm ? parseFloat(formData.height_cm) : null;
      const weightVal = formData.current_weight_kg ? parseFloat(formData.current_weight_kg) : null;
      const payload = {
        ...formData,
        height_cm: heightVal && !isNaN(heightVal) ? heightVal : null,
        current_weight_kg: weightVal && !isNaN(weightVal) ? weightVal : null,
      };

      await client.post('/onboarding/complete', payload);
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding complete error:', error);
      const msg = error.response?.data?.detail || t('errors.generic');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 'welcome':
        return (
          <div className="text-center py-8 space-y-6" data-testid="onboarding-welcome">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/90 mx-auto flex items-center justify-center">
              <Trophy className="h-12 w-12 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: `'${hf}', sans-serif` }}>
                {t('onboarding.welcomeTo').toUpperCase()} <span style={{ fontFamily: "'Outfit', sans-serif" }}>{appLeft}<span style={{ color: pc }}>{appRight}</span></span>
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {t('onboarding.welcomeSubtitle')}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 pt-4">
              <Badge variant="outline" className="px-4 py-2">
                <Dumbbell className="h-4 w-4 mr-2" />
                {t('onboarding.badgePersonalizedWorkouts')}
              </Badge>
              <Badge variant="outline" className="px-4 py-2">
                <Target className="h-4 w-4 mr-2" />
                {t('onboarding.badgeGoalTracking')}
              </Badge>
              <Badge variant="outline" className="px-4 py-2">
                <Activity className="h-4 w-4 mr-2" />
                {t('onboarding.badgeProgressAnalytics')}
              </Badge>
            </div>
          </div>
        );

      case 'basic':
        return (
          <div className="space-y-6" data-testid="onboarding-basic">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('onboarding.tellUsAbout').toUpperCase()} <span className="text-primary">{t('onboarding.yourself').toUpperCase()}</span>
              </h2>
              <p className="text-muted-foreground">{t('onboarding.basicSubtitle')}</p>
            </div>

            <div className="grid gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4" />
                  {t('onboarding.labelGender')}
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'male', label: t('onboarding.genderMale') },
                    { id: 'female', label: t('onboarding.genderFemale') },
                  ].map(g => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => updateField('gender', g.id)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.gender === g.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-foreground/30'
                      }`}
                      data-testid={`gender-${g.id}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4" />
                  {t('onboarding.labelDob')}
                </Label>
                <DateInput
                  value={formData.birth_date}
                  onChange={(e) => updateField('birth_date', e.target.value)}
                  data-testid="birth-date-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Ruler className="h-4 w-4" />
                    {t('onboarding.labelHeight')}
                  </Label>
                  <Input
                    type="number"
                    placeholder="175"
                    value={formData.height_cm}
                    onChange={(e) => updateField('height_cm', e.target.value)}
                    data-testid="height-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Scale className="h-4 w-4" />
                    {t('onboarding.labelWeight')}
                  </Label>
                  <Input
                    type="number"
                    placeholder="75"
                    value={formData.current_weight_kg}
                    onChange={(e) => updateField('current_weight_kg', e.target.value)}
                    data-testid="weight-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-6" data-testid="onboarding-experience">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                YOUR <span className="text-primary">EXPERIENCE</span> LEVEL
              </h2>
              <p className="text-muted-foreground">{t('onboarding.experienceSubtitle')}</p>
            </div>

            <div className="space-y-3">
              {EXPERIENCE_LEVELS.map(level => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => updateField('experience_level', level.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                    formData.experience_level === level.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-foreground/30'
                  }`}
                  data-testid={`experience-${level.id}`}
                >
                  <span className="text-3xl">{level.icon}</span>
                  <div>
                    <p className={`font-semibold ${formData.experience_level === level.id ? 'text-primary' : ''}`}>
                      {level.label}
                    </p>
                    <p className="text-sm text-muted-foreground">{level.desc}</p>
                  </div>
                  {formData.experience_level === level.id && (
                    <Check className="h-5 w-5 text-primary ml-auto" />
                  )}
                </button>
              ))}
            </div>

            <div className="pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4" />
                {t('onboarding.labelFrequency')}
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                {[2, 3, 4, 5, 6].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => updateField('workout_frequency', days)}
                    className={`flex-1 py-3 rounded-lg border text-center font-bold transition-all ${
                      formData.workout_frequency === days
                        ? 'border-primary bg-primary text-white'
                        : 'border-border hover:border-foreground/30'
                    }`}
                    data-testid={`frequency-${days}`}
                  >
                    {days}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">{t('onboarding.daysPerWeek')}</p>
            </div>
          </div>
        );

      case 'goals':
        return (
          <div className="space-y-6" data-testid="onboarding-goals">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                WHAT'S YOUR <span className="text-primary">GOAL</span>?
              </h2>
              <p className="text-muted-foreground">{t('onboarding.goalsSubtitle')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GOALS.map(goal => (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => updateField('primary_goal', goal.id)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    formData.primary_goal === goal.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-foreground/30'
                  }`}
                  data-testid={`goal-${goal.id}`}
                >
                  <span className="text-2xl block mb-2">{goal.icon}</span>
                  <p className={`font-semibold text-sm ${formData.primary_goal === goal.id ? 'text-primary' : ''}`}>
                    {goal.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{goal.desc}</p>
                </button>
              ))}
            </div>

            <div className="pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Target className="h-4 w-4" />
                {t('onboarding.labelTargetAreas')}
              </Label>
              <div className="flex flex-wrap gap-2">
                {TARGET_AREAS.map(area => (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => toggleTargetArea(area.id)}
                    className={`px-4 py-2 rounded-full border transition-all ${
                      formData.target_areas.includes(area.id)
                        ? 'border-primary bg-primary text-white'
                        : 'border-border hover:border-foreground/30'
                    }`}
                    data-testid={`area-${area.id}`}
                  >
                    {area.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6" data-testid="onboarding-preferences">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                WORKOUT <span className="text-primary">PREFERENCES</span>
              </h2>
              <p className="text-muted-foreground">{t('onboarding.preferencesSubtitle')}</p>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-3">
                <Home className="h-4 w-4" />
                {t('onboarding.labelEquipment')}
              </Label>
              <div className="space-y-3">
                {EQUIPMENT_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => updateField('equipment_access', opt.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                      formData.equipment_access === opt.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-foreground/30'
                    }`}
                    data-testid={`equipment-${opt.id}`}
                  >
                    <span className="text-2xl">{opt.icon}</span>
                    <div>
                      <p className={`font-semibold ${formData.equipment_access === opt.id ? 'text-primary' : ''}`}>
                        {opt.label}
                      </p>
                      <p className="text-sm text-muted-foreground">{opt.desc}</p>
                    </div>
                    {formData.equipment_access === opt.id && (
                      <Check className="h-5 w-5 text-primary ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <Label className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4" />
                {t('onboarding.labelDuration')}
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                {[30, 45, 60, 75, 90].map(mins => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => updateField('preferred_workout_duration', mins)}
                    className={`flex-1 py-3 rounded-lg border text-center transition-all ${
                      formData.preferred_workout_duration === mins
                        ? 'border-primary bg-primary text-white font-bold'
                        : 'border-border hover:border-foreground/30'
                    }`}
                    data-testid={`duration-${mins}`}
                  >
                    {mins}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">{t('onboarding.minutesPerSession')}</p>
            </div>
          </div>
        );

      case 'import':
        return (
          <div className="space-y-6" data-testid="onboarding-import">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('import.onboardingTitle', 'COMING FROM ANOTHER APP?')}
              </h2>
              <p className="text-muted-foreground">
                {t('import.onboardingSubtitle', "Import your workout history from Hevy so you don't have to start over.")}
              </p>
            </div>
            <HevyImportWizard onComplete={() => handleNext()} />
            <p className="text-center text-xs text-muted-foreground">
              {t('import.onboardingSkipHint', "You can also do this later from Settings → Export & Import")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="onboarding-page">
      <Card className="w-full max-w-lg border-border/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.id}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      idx < currentStep
                        ? 'bg-green-500 text-white'
                        : idx === currentStep
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {idx < currentStep ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleSkip}
              className="min-h-[44px] px-3 text-sm text-muted-foreground hover:text-foreground active:bg-muted rounded transition-colors"
              data-testid="skip-onboarding-btn"
            >
              {t('onboarding.btnSkip')}
            </button>
          </div>
          <Progress value={progress} className="h-1.5" />
        </CardHeader>

        <CardContent className="pb-6">
          {renderStep()}

          <div className="flex gap-3 mt-8">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1"
                data-testid="onboarding-back-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t('onboarding.btnBack')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className={`flex-1 bg-primary hover:bg-primary/90 ${currentStep === 0 ? 'w-full' : ''}`}
              data-testid="onboarding-next-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  {t('onboarding.btnComplete')}
                </>
              ) : (
                <>
                  {currentStep === 0 ? t('onboarding.btnLetsGo') : t('onboarding.btnNext')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
